import React, { useEffect, useMemo, useState } from "react";
import ActivepatientApi from "../APIs/activePatientApi";

const RECORD_TYPES = ["Consultation", "Diagnosis", "Lab Result", "Treatment"];

export default function MedicalRecordModal({
  isOpen,
  onClose,
  mode = "create", // 'create' | 'edit'
  initialData = null, // for edit: { recordId, patientId, providerId, visitDate, recordType, summary, notes }
  patientId,
  providerId,
  onSaved, // (savedRecord) => void
}) {
  const [form, setForm] = useState({
    visitDate: "",
    recordType: "",
    summary: "",
    notes: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [errMsg, setErrMsg] = useState("");

  useEffect(() => {
    if (isOpen && mode === "edit" && initialData) {
      setForm({
        visitDate: initialData.visitDate ?? "",
        recordType: initialData.recordType ?? "",
        summary: initialData.summary ?? "",
        notes: initialData.notes ?? "",
      });
      setErrMsg("");
    }
    if (isOpen && mode === "create") {
      setForm({ visitDate: "", recordType: "", summary: "", notes: "" });
      setErrMsg("");
    }
  }, [isOpen, mode, initialData]);

  const title = useMemo(
    () => (mode === "edit" ? "Edit Medical Record" : "Add Medical Record"),
    [mode],
  );

  if (!isOpen) return null;

  const onChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrMsg("");
    if (!form.visitDate || !form.recordType.trim() || !form.summary.trim()) {
      setErrMsg("Visit Date, Record Type, and Summary are required.");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        // MedicalRecordRequestDTO shape assumption:
        // Long patientId; Long providerId; LocalDate visitDate; String recordType; String summary; String notes;
        patientId,
        providerId,
        visitDate: form.visitDate || null,
        recordType: form.recordType.trim(),
        summary: form.summary.trim(),
        notes: form.notes || null,
      };

      let saved;
      if (mode === "create") {
        saved = await ActivepatientApi.createMedicalRecord(payload);
      } else {
        saved = await ActivepatientApi.updateMedicalRecord(
          initialData.recordId,
          payload,
        );
      }

      onSaved?.(saved);
      onClose?.();
    } catch (err) {
      const validationErrors = err?.response?.data?.errors || err?.body?.errors;
      const details = validationErrors
        ? Object.entries(validationErrors)
            .map(([k, v]) => `${k}: ${v}`)
            .join("\n")
        : "";
      const message =
        err?.response?.data?.message ||
        err.message ||
        "Failed to save medical record";
      setErrMsg(`${message}${details ? `\n${details}` : ""}`);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="w-full max-w-lg rounded-2xl bg-white p-6 shadow-xl">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold">{title}</h3>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
          >
            ✕
          </button>
        </div>

        {errMsg && (
          <div className="mb-3 rounded border border-red-200 bg-red-50 p-2 text-sm text-red-700 whitespace-pre-line">
            {errMsg}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium">Visit Date</label>
            <input
              type="date"
              name="visitDate"
              value={form.visitDate}
              onChange={onChange}
              className="mt-1 w-full rounded border px-3 py-2"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium">Record Type</label>
            <select
              name="recordType"
              value={form.recordType}
              onChange={onChange}
              className="mt-1 w-full rounded border px-3 py-2"
              required
            >
              <option value="">Select record type</option>
              {RECORD_TYPES.map((type) => (
                <option key={type} value={type}>
                  {type}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium">Summary</label>
            <input
              type="text"
              name="summary"
              value={form.summary}
              onChange={onChange}
              className="mt-1 w-full rounded border px-3 py-2"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium">Notes</label>
            <textarea
              name="notes"
              value={form.notes}
              onChange={onChange}
              rows={4}
              className="mt-1 w-full rounded border px-3 py-2"
            />
          </div>

          <div className="flex items-center justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="rounded px-4 py-2 text-sm bg-gray-100 hover:bg-gray-200"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="rounded px-4 py-2 text-sm text-white bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60"
            >
              {submitting ? "Saving…" : "Save"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
